'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  ExternalLink,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Shield,
  Hospital,
  TrendingUp,
  Clock,
  Star,
  Info,
  GitCompare,
  MessageSquare,
  ThumbsUp,
  Send,
} from 'lucide-react';
import {
  INSURERS,
  formatCurrency,
  formatPremium,
  getCSRBadgeClass,
  getCSRLabel,
  type Policy,
} from '@/lib/policyData';
import CSRChart from './CSRChart';
import DisclaimerPopup from '@/components/ui/DisclaimerPopup';

interface PolicyDetailViewProps {
  policy: Policy;
}

type Tab = 'overview' | 'coverage' | 'exclusions' | 'claims';

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'coverage', label: "What's Covered" },
  { key: 'exclusions', label: 'Exclusions' },
  { key: 'claims', label: 'Claims & Network' },
];

const FAQ_ITEMS = [
  {
    q: 'How do I file a cashless claim?',
    a: 'Inform the insurer at least 48 hours before planned hospitalisation (4 hours for emergencies). Show your health card at the network hospital — no upfront payment required.',
  },
  {
    q: 'What is the free look period?',
    a: 'You have 15 days from policy receipt to review and return the policy for a full refund if you are not satisfied with the terms.',
  },
  {
    q: 'Can I add family members mid-term?',
    a: 'Newborns can be added within 90 days of birth. Other family members can typically be added only at renewal.',
  },
  {
    q: 'How does the Section 80D tax benefit work?',
    a: 'Premiums paid for health insurance qualify for deduction under Section 80D — up to ₹25,000 for self/family and ₹50,000 if the insured is a senior citizen.',
  },
  {
    q: 'What happens if my claim is rejected?',
    a: 'You can escalate to the insurer\'s grievance cell, then to the Insurance Ombudsman, and finally to IRDAI\'s IGMS portal for resolution.',
  },
];

interface Comment {
  id: string;
  name: string;
  rating: number;
  text: string;
  date: string;
  helpful: number;
}

const INITIAL_COMMENTS: Comment[] = [
  {
    id: 'c1',
    name: 'Rajesh M.',
    rating: 5,
    text: 'Excellent plan! The cashless claim process was smooth and the hospital network is very wide. Highly recommend for families.',
    date: 'July 2025',
    helpful: 12,
  },
  {
    id: 'c2',
    name: 'Priya S.',
    rating: 4,
    text: 'Good coverage and competitive premium. The waiting period for pre-existing conditions is standard. Customer support was helpful.',
    date: 'June 2025',
    helpful: 8,
  },
  {
    id: 'c3',
    name: 'Amit K.',
    rating: 4,
    text: 'Compared multiple plans on MediCompare and this one offered the best value for money. The CSR is impressive.',
    date: 'May 2025',
    helpful: 5,
  },
];

export default function PolicyDetailView({ policy }: PolicyDetailViewProps) {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [comments, setComments] = useState<Comment[]>(INITIAL_COMMENTS);
  const [newComment, setNewComment] = useState({ name: '', rating: 5, text: '' });
  const [helpfulClicked, setHelpfulClicked] = useState<Set<string>>(new Set());
  const insurer = INSURERS[policy.insurer];

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.name.trim() || !newComment.text.trim()) return;
    const comment: Comment = {
      id: `c-${Date.now()}`,
      name: newComment.name.trim(),
      rating: newComment.rating,
      text: newComment.text.trim(),
      date: new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
      helpful: 0,
    };
    setComments((prev) => [comment, ...prev]);
    setNewComment({ name: '', rating: 5, text: '' });
  };

  const handleHelpful = (id: string) => {
    if (helpfulClicked.has(id)) return;
    setHelpfulClicked((prev) => new Set(prev).add(id));
    setComments((prev) =>
      prev.map((c) => (c.id === id ? { ...c, helpful: c.helpful + 1 } : c))
    );
  };

  const avgRating = comments.length > 0
    ? (comments.reduce((sum, c) => sum + c.rating, 0) / comments.length).toFixed(1)
    : '0.0';

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Link href="/policy-listing-filters" className="flex items-center gap-1.5 hover:text-primary transition-colors">
          <ArrowLeft className="w-4 h-4" />
          All Plans
        </Link>
        <span>/</span>
        <span className="text-foreground font-medium">{policy.planName}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 xl:gap-8">
        {/* Main content — 2/3 */}
        <div className="lg:col-span-2 space-y-5">
          {/* Hero card */}
          <div className="bg-card border border-border rounded-2xl p-6 card-shadow">
            {/* Top row */}
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-extrabold text-sm flex-shrink-0 ${insurer.bgColor} ${insurer.color}`}>
                  {insurer.abbr}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-medium">{insurer.name}</p>
                  <h1 className="text-xl font-extrabold text-foreground leading-tight">{policy.planName}</h1>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs font-medium px-2 py-0.5 bg-muted text-muted-foreground rounded-full">
                      {policy.policyType}
                    </span>
                    {policy.tag && (
                      <span className="text-xs font-semibold px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                        {policy.tag}
                      </span>
                    )}
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getCSRBadgeClass(policy.claimSettlementRatio)}`}>
                      {getCSRLabel(policy.claimSettlementRatio)} CSR
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-2xl font-extrabold text-primary font-tabular">{formatPremium(policy.premium)}</p>
                <p className="text-xs text-muted-foreground">per year</p>
              </div>
            </div>

            {/* Key stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                {
                  icon: TrendingUp,
                  label: 'Claim Settlement',
                  value: `${policy.claimSettlementRatio}%`,
                  color: 'text-[var(--success)]',
                  bg: 'bg-[var(--success-bg)]',
                },
                {
                  icon: Hospital,
                  label: 'Network Hospitals',
                  value: policy.networkHospitals >= 1000
                    ? `${(policy.networkHospitals / 1000).toFixed(0)},000+`
                    : `${policy.networkHospitals}`,
                  color: 'text-primary',
                  bg: 'bg-primary/10',
                },
                {
                  icon: Shield,
                  label: 'Sum Insured',
                  value: formatCurrency(policy.coverageAmount),
                  color: 'text-accent',
                  bg: 'bg-accent/10',
                },
                {
                  icon: Clock,
                  label: 'Waiting Period',
                  value: `${policy.waitingPeriodYears} Year${policy.waitingPeriodYears !== 1 ? 's' : ''}`,
                  color: 'text-amber-600',
                  bg: 'bg-amber-50',
                },
              ].map((stat) => (
                <div
                  key={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`${stat.bg} rounded-xl p-3 flex items-center gap-2.5`}
                >
                  <stat.icon className={`w-5 h-5 ${stat.color} flex-shrink-0`} />
                  <div>
                    <p className="text-2xs text-muted-foreground font-medium">{stat.label}</p>
                    <p className={`text-sm font-extrabold ${stat.color} font-tabular`}>{stat.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Tax benefit callout */}
            {policy.taxBenefit80D && (
              <div className="mt-4 flex items-center gap-2.5 px-4 py-3 bg-[var(--info-bg)] border border-primary/20 rounded-xl">
                <Star className="w-4 h-4 text-primary flex-shrink-0" />
                <p className="text-xs text-primary font-medium">
                  <strong>Section 80D Tax Benefit:</strong> Save up to ₹25,000 (₹50,000 for senior citizens) in income tax by investing in this health plan.
                </p>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden card-shadow">
            {/* Tab nav */}
            <div className="flex border-b border-border overflow-x-auto scrollbar-thin">
              {TABS.map((tab) => (
                <button
                  key={`tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-shrink-0 px-5 py-3.5 text-sm font-semibold transition-all duration-150 border-b-2 ${
                    activeTab === tab.key
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="p-6">
              {/* Overview tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-bold text-foreground mb-3">Plan Highlights</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {policy.highlights.map((h, i) => (
                        <div
                          key={`highlight-detail-${i}`}
                          className="flex items-start gap-2.5 p-3 bg-[var(--success-bg)] rounded-xl"
                        >
                          <CheckCircle className="w-4 h-4 text-[var(--success)] flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground">{h}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-foreground mb-3">Policy Details</h3>
                    <div className="border border-border rounded-xl overflow-hidden">
                      {[
                        { label: 'Policy Type', value: policy.policyType },
                        { label: 'Annual Premium', value: formatPremium(policy.premium) },
                        { label: 'Sum Insured', value: formatCurrency(policy.coverageAmount) },
                        { label: 'Room Rent Limit', value: policy.roomRentLimit },
                        { label: 'Co-Payment', value: policy.coPayment },
                        { label: 'Daycare Procedures', value: policy.daycareProceduress > 0 ? `${policy.daycareProceduress}+ procedures` : 'Not applicable' },
                        { label: 'Waiting Period (PED)', value: `${policy.waitingPeriodYears} year${policy.waitingPeriodYears !== 1 ? 's' : ''}` },
                        { label: 'Network Hospitals', value: `${policy.networkHospitals.toLocaleString('en-IN')}+ cashless hospitals` },
                        { label: 'Claim Settlement Ratio', value: `${policy.claimSettlementRatio}% (IRDAI 2023–24)` },
                      ].map((row, i) => (
                        <div
                          key={`detail-row-${i}`}
                          className={`flex items-center justify-between px-4 py-3 ${
                            i % 2 === 0 ? 'bg-muted/30' : 'bg-card'
                          } border-b border-border/50 last:border-b-0`}
                        >
                          <span className="text-sm text-muted-foreground">{row.label}</span>
                          <span className="text-sm font-semibold text-foreground">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-foreground mb-3">Feature Availability</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[
                        { label: 'Restoration Benefit', value: policy.restoration },
                        { label: 'Maternity Cover', value: policy.maternity },
                        { label: 'OPD Consultation', value: policy.opd },
                        { label: 'Critical Illness', value: policy.criticalIllness },
                        { label: 'Ambulance Cover', value: policy.ambulanceCover },
                        { label: 'Annual Checkup', value: policy.annualHealthCheckup },
                        { label: 'No Claim Bonus', value: policy.noClaimBonus },
                        { label: 'Cashless Available', value: policy.cashlessAvailable },
                        { label: 'Pre-existing Cover', value: policy.preExistingCover },
                      ].map((feat) => (
                        <div
                          key={`feat-detail-${feat.label.toLowerCase().replace(/\s/g, '-')}`}
                          className={`flex items-center gap-2 p-2.5 rounded-lg border ${
                            feat.value
                              ? 'border-[var(--success)] bg-[var(--success-bg)]'
                              : 'border-border bg-muted/30'
                          }`}
                        >
                          {feat.value ? (
                            <CheckCircle className="w-4 h-4 text-[var(--success)] flex-shrink-0" />
                          ) : (
                            <XCircle className="w-4 h-4 text-[var(--danger)] flex-shrink-0" />
                          )}
                          <span className="text-xs font-medium text-foreground">{feat.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Coverage tab */}
              {activeTab === 'coverage' && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-sm font-bold text-foreground mb-3">Covered Expenses</h3>
                    <div className="flex flex-col gap-2">
                      {[
                        'In-patient hospitalisation (room, nursing, ICU)',
                        'Pre-hospitalisation expenses (up to 60 days)',
                        'Post-hospitalisation expenses (up to 90 days)',
                        `${policy.daycareProceduress > 0 ? `${policy.daycareProceduress}+` : 'Listed'} daycare procedures`,
                        'Organ donor expenses',
                        'Road ambulance charges',
                        ...(policy.restoration ? ['Sum insured restoration on exhaustion'] : []),
                        ...(policy.maternity ? ['Maternity expenses (after 2-year waiting period)'] : []),
                        ...(policy.opd ? ['OPD doctor consultations and diagnostics'] : []),
                        ...(policy.criticalIllness ? ['Critical illness lump sum payout'] : []),
                        ...(policy.annualHealthCheckup ? ['Free annual health check-up'] : []),
                        'AYUSH treatment (Ayurveda, Yoga, Unani, Siddha, Homeopathy)',
                        'Domiciliary hospitalisation (if admitted at home)',
                        'Emergency domestic medical evacuation',
                      ].map((item, i) => (
                        <div
                          key={`coverage-item-${i}`}
                          className="flex items-start gap-2.5 px-4 py-3 bg-[var(--success-bg)] rounded-xl"
                        >
                          <CheckCircle className="w-4 h-4 text-[var(--success)] flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-amber-800 mb-1">Sub-limits Apply</p>
                        <p className="text-xs text-amber-700">
                          Room rent is capped at <strong>{policy.roomRentLimit}</strong>. Exceeding the room rent limit may proportionately reduce your reimbursement for all associated expenses.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Exclusions tab */}
              {activeTab === 'exclusions' && (
                <div className="space-y-5">
                  <div className="p-4 bg-[var(--danger-bg)] border border-[var(--danger)]/20 rounded-xl">
                    <p className="text-sm text-[var(--danger)] font-medium">
                      The following conditions and treatments are <strong>not covered</strong> under this policy. Always read the full policy document before purchasing.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-foreground mb-3">Permanent Exclusions</h3>
                    <div className="flex flex-col gap-2">
                      {[
                        ...policy.exclusions,
                        'Intentional self-injury or suicide attempt',
                        'Treatment outside India (unless emergency)',
                        'Circumcision (unless medically necessary)',
                        'Refractive error correction (spectacles/contact lenses)',
                        'Congenital external diseases or defects',
                        'Sexually transmitted diseases (unless covered by rider)',
                        'Expenses arising from nuclear or chemical contamination',
                      ].map((item, i) => (
                        <div
                          key={`exclusion-${i}`}
                          className="flex items-start gap-2.5 px-4 py-3 bg-[var(--danger-bg)] rounded-xl"
                        >
                          <XCircle className="w-4 h-4 text-[var(--danger)] flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-foreground mb-3">Waiting Periods</h3>
                    <div className="border border-border rounded-xl overflow-hidden">
                      {[
                        { condition: 'Pre-existing diseases (PED)', period: `${policy.waitingPeriodYears} years` },
                        { condition: 'Specific diseases (hernia, cataract, etc.)', period: '2 years' },
                        { condition: 'Maternity expenses', period: policy.maternity ? '2 years' : 'Not covered' },
                        { condition: 'Initial waiting period (all illnesses)', period: '30 days' },
                        { condition: 'Accidental injuries', period: 'No waiting period' },
                      ].map((row, i) => (
                        <div
                          key={`waiting-${i}`}
                          className={`flex items-center justify-between px-4 py-3 border-b border-border/50 last:border-b-0 ${
                            i % 2 === 0 ? 'bg-muted/30' : 'bg-card'
                          }`}
                        >
                          <span className="text-sm text-foreground">{row.condition}</span>
                          <span className={`text-sm font-semibold ${
                            row.period === 'No waiting period' ?'text-[var(--success)]'
                              : row.period === 'Not covered' ?'text-[var(--danger)]' :'text-amber-600'
                          }`}>
                            {row.period}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Claims & Network tab */}
              {activeTab === 'claims' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-bold text-foreground mb-3">Claim Settlement Performance</h3>
                    <CSRChart csr={policy.claimSettlementRatio} insurerName={policy.insurerName} />
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-foreground mb-3">How to File a Claim</h3>
                    <div className="flex flex-col gap-3">
                      {[
                        {
                          step: '01',
                          title: 'Intimate the Insurer',
                          desc: 'Call the 24×7 helpline or use the mobile app within 48 hours of planned admission (4 hours for emergencies).',
                        },
                        {
                          step: '02',
                          title: 'Cashless at Network Hospital',
                          desc: 'Show your health card at the hospital TPA desk. Pre-authorisation is processed within 1 hour for planned procedures.',
                        },
                        {
                          step: '03',
                          title: 'Submit Documents',
                          desc: 'Provide discharge summary, bills, reports, and your policy number. Claims are processed within 7 working days.',
                        },
                        {
                          step: '04',
                          title: 'Reimbursement (Non-network)',
                          desc: 'For non-network hospitals, pay upfront and submit all original bills within 30 days for reimbursement.',
                        },
                      ].map((step) => (
                        <div
                          key={`claim-step-${step.step}`}
                          className="flex items-start gap-3 p-4 bg-muted/30 rounded-xl"
                        >
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-extrabold text-primary">{step.step}</span>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">{step.title}</p>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{step.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-foreground mb-3">Hospital Network</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[
                        { city: 'Mumbai', count: Math.floor(policy.networkHospitals * 0.08) },
                        { city: 'Delhi NCR', count: Math.floor(policy.networkHospitals * 0.09) },
                        { city: 'Bengaluru', count: Math.floor(policy.networkHospitals * 0.06) },
                        { city: 'Chennai', count: Math.floor(policy.networkHospitals * 0.05) },
                        { city: 'Hyderabad', count: Math.floor(policy.networkHospitals * 0.05) },
                        { city: 'Pune', count: Math.floor(policy.networkHospitals * 0.04) },
                      ].map((city) => (
                        <div
                          key={`city-${city.city.toLowerCase().replace(/\s/g, '-')}`}
                          className="flex items-center justify-between px-3 py-2.5 bg-card border border-border rounded-xl"
                        >
                          <div className="flex items-center gap-2">
                            <Hospital className="w-3.5 h-3.5 text-primary" />
                            <span className="text-sm font-medium text-foreground">{city.city}</span>
                          </div>
                          <span className="text-sm font-bold text-primary font-tabular">{city.count}+</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Total {policy.networkHospitals.toLocaleString('en-IN')}+ hospitals across India. Find hospitals at {policy.insurerName}&apos;s official website.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* FAQ */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden card-shadow">
            <div className="px-6 py-4 border-b border-border">
              <h2 className="text-base font-bold text-foreground">Frequently Asked Questions</h2>
            </div>
            <div className="divide-y divide-border">
              {FAQ_ITEMS.map((faq, i) => (
                <div key={`faq-detail-${i}`}>
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/30 transition-colors"
                  >
                    <span className="text-sm font-semibold text-foreground pr-4">{faq.q}</span>
                    {expandedFaq === i ? (
                      <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    )}
                  </button>
                  {expandedFaq === i && (
                    <div className="px-6 pb-4 animate-fade-in">
                      <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* User Comments & Reviews */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden card-shadow">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <MessageSquare className="w-5 h-5 text-primary" />
                <h2 className="text-base font-bold text-foreground">User Reviews</h2>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={`avg-star-${star}`}
                      className={`w-4 h-4 ${parseFloat(avgRating) >= star ? 'text-amber-400 fill-amber-400' : 'text-border'}`}
                    />
                  ))}
                </div>
                <span className="text-sm font-bold text-foreground">{avgRating}</span>
                <span className="text-xs text-muted-foreground">({comments.length} reviews)</span>
              </div>
            </div>

            {/* Existing comments */}
            <div className="divide-y divide-border">
              {comments.map((comment) => (
                <div key={comment.id} className="px-6 py-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-primary">{comment.name.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{comment.name}</p>
                        <p className="text-xs text-muted-foreground">{comment.date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={`star-${comment.id}-${star}`}
                          className={`w-3.5 h-3.5 ${comment.rating >= star ? 'text-amber-400 fill-amber-400' : 'text-border'}`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{comment.text}</p>
                  <button
                    onClick={() => handleHelpful(comment.id)}
                    disabled={helpfulClicked.has(comment.id)}
                    className={`flex items-center gap-1.5 text-xs transition-colors ${
                      helpfulClicked.has(comment.id)
                        ? 'text-primary font-medium' :'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <ThumbsUp className="w-3.5 h-3.5" />
                    Helpful ({comment.helpful})
                  </button>
                </div>
              ))}
            </div>

            {/* Add comment form */}
            <div className="px-6 py-5 border-t border-border bg-muted/20">
              <h3 className="text-sm font-bold text-foreground mb-4">Write a Review</h3>
              <form onSubmit={handleSubmitComment} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Your Name</label>
                    <input
                      type="text"
                      value={newComment.name}
                      onChange={(e) => setNewComment((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Rahul S."
                      maxLength={40}
                      className="w-full px-3 py-2 text-sm bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors placeholder:text-muted-foreground/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Rating</label>
                    <div className="flex items-center gap-1 h-9">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={`rate-star-${star}`}
                          type="button"
                          onClick={() => setNewComment((prev) => ({ ...prev, rating: star }))}
                          className="p-0.5 transition-transform hover:scale-110"
                        >
                          <Star
                            className={`w-6 h-6 transition-colors ${
                              newComment.rating >= star ? 'text-amber-400 fill-amber-400' : 'text-border hover:text-amber-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Your Review</label>
                  <textarea
                    value={newComment.text}
                    onChange={(e) => setNewComment((prev) => ({ ...prev, text: e.target.value }))}
                    placeholder="Share your experience with this plan..."
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 text-sm bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none placeholder:text-muted-foreground/50"
                  />
                </div>
                <button
                  type="submit"
                  disabled={!newComment.name.trim() || !newComment.text.trim()}
                  className="btn-primary py-2 px-5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-3.5 h-3.5" />
                  Submit Review
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar — 1/3 */}
        <div className="space-y-4">
          {/* Sticky buy card */}
          <div className="sticky top-20 space-y-4">
            <div className="bg-card border-2 border-primary/30 rounded-2xl p-5 card-shadow">
              <div className="text-center mb-4">
                <p className="text-xs text-muted-foreground font-medium mb-1">Annual Premium</p>
                <p className="text-3xl font-extrabold text-primary font-tabular">{formatPremium(policy.premium)}</p>
                <p className="text-xs text-muted-foreground">Sum Insured: {formatCurrency(policy.coverageAmount)}</p>
              </div>

              <button
                onClick={() => setShowDisclaimer(true)}
                className="btn-primary w-full justify-center text-base py-3 mb-3"
              >
                Buy from {insurer.name}
                <ExternalLink className="w-4 h-4" />
              </button>

              <Link
                href={`/policy-comparison?ids=policy-star-001,${policy.id}`}
                className="btn-outline w-full justify-center text-sm py-2.5 mb-4"
              >
                <GitCompare className="w-4 h-4" />
                Compare with Others
              </Link>

              <div className="flex items-start gap-2 p-3 bg-[var(--success-bg)] rounded-xl">
                <Shield className="w-4 h-4 text-[var(--success)] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[var(--success)] leading-relaxed">
                  You&apos;ll be redirected to {insurer.name}&apos;s official website. MediCompare does not collect your payment.
                </p>
              </div>
            </div>

            {/* Quick facts */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="text-sm font-bold text-foreground mb-3">Quick Facts</h3>
              <div className="flex flex-col gap-2.5">
                {[
                  { label: 'Insurer', value: insurer.name },
                  { label: 'CSR (2023–24)', value: `${policy.claimSettlementRatio}%` },
                  { label: 'Cashless Hospitals', value: `${(policy.networkHospitals / 1000).toFixed(0)}K+` },
                  { label: 'Waiting Period', value: `${policy.waitingPeriodYears} year${policy.waitingPeriodYears !== 1 ? 's' : ''}` },
                  { label: 'Daycare Procedures', value: policy.daycareProceduress > 0 ? `${policy.daycareProceduress}+` : 'N/A' },
                  { label: 'Co-Payment', value: policy.coPayment },
                  { label: 'Section 80D', value: policy.taxBenefit80D ? 'Eligible' : 'Not eligible' },
                ].map((fact) => (
                  <div
                    key={`fact-${fact.label.toLowerCase().replace(/\s/g, '-')}`}
                    className="flex items-center justify-between"
                  >
                    <span className="text-xs text-muted-foreground">{fact.label}</span>
                    <span className="text-xs font-semibold text-foreground">{fact.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Similar plans */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="text-sm font-bold text-foreground mb-3">Similar Plans</h3>
              <div className="flex flex-col gap-2">
                {[
                  { id: 'policy-hdfc-001', name: 'Optima Secure', insurer: 'HDFC ERGO', premium: 10800 },
                  { id: 'policy-niva-001', name: 'ReAssure 2.0', insurer: 'Niva Bupa', premium: 9500 },
                  { id: 'policy-care-001', name: 'Care Supreme', insurer: 'Care Health', premium: 11500 },
                ].filter((p) => p.id !== policy.id).slice(0, 2).map((p) => (
                  <Link
                    key={`similar-${p.id}`}
                    href={`/policy-detail?id=${p.id}`}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-xl hover:bg-primary/5 hover:border-primary/20 border border-transparent transition-all duration-150"
                  >
                    <div>
                      <p className="text-xs text-muted-foreground">{p.insurer}</p>
                      <p className="text-sm font-semibold text-foreground">{p.name}</p>
                    </div>
                    <p className="text-sm font-bold text-primary font-tabular">
                      {formatPremium(p.premium)}/yr
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer Popup */}
      <DisclaimerPopup
        isOpen={showDisclaimer}
        onClose={() => setShowDisclaimer(false)}
        buyUrl={policy.buyUrl}
        insurerName={insurer.name}
      />
    </div>
  );
}